import dayjs from 'dayjs';

export function filterRows(rows = [], { search = '', question = 'none', position = 'all' } = {}) {
  const ranges = { 1: [5, 10], 2: [10, 20], 3: [20, 30] };
  const range = ranges[position];
  return rows.filter(row => {
    const text = (row.keys?.[0] || '').toLowerCase();
    const questionMatches = !question || question === 'none' || (question === 'all'
      ? /^(who|what|where|when|why|how|can|does)\s/.test(text)
      : text.startsWith(`${question.toLowerCase()} `));
    return text.includes(search.trim().toLowerCase()) && questionMatches && (!range || (row.position >= range[0] && row.position <= range[1]));
  });
}

export function sortRows(rows = [], key = 'clicks', direction = 'desc') {
  return [...rows].sort((a, b) => {
    const comparison = key === 'keys'
      ? (a.keys?.[0] || '').localeCompare(b.keys?.[0] || '', 'en', { sensitivity: 'base' })
      : (Number(a[key]) || 0) - (Number(b[key]) || 0);
    return direction === 'asc' ? comparison : -comparison;
  });
}

export function paginateRows(rows, requestedPage = 1, size = 10) {
  const totalPages = Math.max(1, Math.ceil(rows.length / size));
  const page = Math.min(totalPages, Math.max(1, Number(requestedPage) || 1));
  return { rows: rows.slice((page - 1) * size, page * size), page, totalPages, total: rows.length };
}

export function summarizeMetrics(rows = []) {
  const totals = rows.reduce((sum, row) => ({
    clicks: sum.clicks + (Number(row.clicks) || 0),
    impressions: sum.impressions + (Number(row.impressions) || 0),
    weightedPosition: sum.weightedPosition + (Number(row.position) || 0) * (Number(row.impressions) || 0),
  }), { clicks: 0, impressions: 0, weightedPosition: 0 });
  return { clicks: totals.clicks, impressions: totals.impressions, ctr: totals.impressions ? totals.clicks / totals.impressions : 0, position: totals.impressions ? totals.weightedPosition / totals.impressions : 0 };
}

export function chartData(pages = [], queries = [], metric = 'clicks') {
  const byDate = new Map();
  for (const [label, rows] of [['Page', pages], ['Query', queries]]) {
    for (const row of rows) {
      const date = row.keys[0];
      const entry = byDate.get(date) || { date, Page: 0, Query: 0 };
      entry[label] = (Number(row[metric]) || 0) * (metric === 'ctr' ? 100 : 1);
      byDate.set(date, entry);
    }
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export const datePresets = [
  ['yesterday', 'Latest available day'], ['three_days', 'Last 3 days'], ['seven_days', 'Last 7 days'],
  ['two_weeks', 'Last 2 weeks'], ['last_month', 'Last 30 days'], ['three_months', 'Last 3 months'],
  ['six_months', 'Last 6 months'], ['one_year', 'Last year'], ['all_time', 'Last 16 months'], ['custom', 'Custom range'],
].map(([value, label]) => ({ value, label }));

export function datePreset(preset = 'two_weeks', now = new Date()) {
  const today = dayjs(now).startOf('day');
  const days = { yesterday: 1, three_days: 3, seven_days: 7, two_weeks: 14, last_month: 30, three_months: 90, six_months: 180 };
  const start = preset === 'all_time' ? today.subtract(16, 'month') : preset === 'one_year' ? today.subtract(1, 'year') : today.subtract((days[preset] || 14) + 1, 'day');
  return [start.format('YYYY-MM-DD'), today.subtract(2, 'day').format('YYYY-MM-DD')];
}

export function toCsv(rows) {
  return rows.map(row => row.map(value => {
    let text = value == null ? '' : String(value);
    if (typeof value === 'string' && /^[\s]*[=+\-@\t\r]/.test(text)) text = `'${text}`;
    return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  }).join(',')).join('\r\n');
}
